import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import GlassCard from "@/components/GlassCard";
import LogMaintenanceSheet from "@/components/LogMaintenanceSheet";
import PaintEditSheet from "@/components/PaintEditSheet";
import AddApplianceSheet from "@/components/AddApplianceSheet";
import AddPaintSheet from "@/components/AddPaintSheet";
import AddFurnitureSheet from "@/components/AddFurnitureSheet";
import AddPlantSheet from "@/components/AddPlantSheet";
import SpeedDialFAB from "@/components/SpeedDialFAB";
import { useAppliances } from "@/hooks/useAppliances";
import { useRoutineTasks } from "@/hooks/useRoutineTasks";
import { ROUTINE_CATEGORIES } from "@/constants/RoutineSuggestions";
import { CheckCircle2, AlertCircle, ChevronLeft, ChefHat, Shirt, Wind as WindIcon, TreePine } from "lucide-react";
import { usePaintCodes } from "@/hooks/usePaintCodes";
import { useServiceLogs } from "@/hooks/useServiceLogs";
import { useFurniture } from "@/hooks/useFurniture";
import { usePlants } from "@/hooks/usePlants";
import { useHomeLibrary } from "@/hooks/useHomeLibrary";
import { usePropertyContext } from "@/contexts/PropertyContext";
import { useSubscription } from "@/hooks/useSubscription";
import QuickInfoTile from "@/components/QuickInfoTile";
import QuickInfoEditSheet from "@/components/QuickInfoEditSheet";
import UpgradePlanSheet from "@/components/UpgradePlanSheet";
import { useQuickInfo } from "@/hooks/useQuickInfo";
import ExpandHeader from "@/components/ExpandHeader";
import { Paintbrush, Refrigerator, Wrench, Edit2, Loader2, Plus, Droplets, Zap, Home, Info, FileText, Sofa, Trash2, ExternalLink, Lock, Sprout, Sun, Calendar, PenTool } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import SwipeableCard from "@/components/SwipeableCard";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { useModalHistory } from "@/hooks/useModalHistory";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";


const CategoryIcon = ({ icon: Icon, label, onClick, locked, color = "text-primary", bg = "bg-primary/10", className }) => (
    <div 
        onClick={locked ? undefined : onClick}
        className={cn(
            "flex flex-col items-center justify-center p-4 bg-white/50 backdrop-blur-md border border-white/20 rounded-[2rem] shadow-sm hover:shadow-md transition-all cursor-pointer active:scale-95 w-full h-[115px]",
            locked && "opacity-60 grayscale",
            className
        )}
    >
        <div className={cn("p-3 rounded-xl mb-2 relative", bg)}>
            <Icon className={cn("w-6 h-6", color)} />
            {locked && (
                <div className="absolute -bottom-1 -right-1 bg-amber-500 text-white p-0.5 rounded-full shadow-sm">
                    <Lock className="w-2.5 h-2.5" />
                </div>
            )}
        </div>
        <span className="text-xs font-bold text-foreground text-center leading-tight">{label}</span>
    </div>
);

const HomeRecords = () => {
    const navigate = useNavigate();
    const { selectedProperty, properties, setActiveProperty } = usePropertyContext();
    const activeProperty = selectedProperty;

    const { appliances, loading: appliancesLoading, addAppliance, deleteAppliance, updateAppliance, refetch: refetchAppliances } = useAppliances(activeProperty?.id);
    const { paintCodes, loading: paintLoading, updatePaintCode, addPaintCode, deletePaintCode } = usePaintCodes(activeProperty?.id);
    const { furniture, loading: furnitureLoading, refetch: refetchFurniture, deleteFurniture, updateFurniture } = useFurniture(activeProperty?.id);
    const { plants, loading: plantsLoading, refetch: refetchPlants, deletePlant, markAsWatered, updatePlant } = usePlants(activeProperty?.id);
    const { serviceLogs, loading: logsLoading, refetch: refetchServiceLogs, deleteServiceLog } = useServiceLogs(activeProperty?.id);
    const { quickInfo, upsertQuickInfo, deleteQuickInfo, loading: quickInfoLoading } = useQuickInfo(activeProperty?.id);
    const { libraryItems } = useHomeLibrary(activeProperty?.id);
    const { isFeatureLocked, isPropertyLocked, plan, hasPremium } = useSubscription();
    const { routineTasks, addRoutineTask } = useRoutineTasks();

    // Routine Care categorization logic is now handled in the popup render

    const propertyLocked = isPropertyLocked(activeProperty?.id);

    const combinedVaultItems = [...(libraryItems || []), ...(furniture || []), ...(appliances || [])]
        .filter(item => item.created_at)
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

    const isItemGloballyLocked = (id) => {
        if (hasPremium) return false;
        const index = combinedVaultItems.findIndex(item => item.id === id);
        return index >= 5;
    };

    const sortedPlants = [...(plants || [])]
        .filter(item => item.created_at)
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

    const isPlantLocked = (id) => {
        if (hasPremium) return false;
        const index = sortedPlants.findIndex(item => item.id === id);
        return index >= 2;
    };

    const sortedLogs = [...(serviceLogs || [])]
        .filter(item => item.created_at || item.date)
        .sort((a, b) => new Date(a.created_at || a.date) - new Date(b.created_at || b.date));

    const isLogLocked = (id) => {
        if (hasPremium) return false;
        const index = sortedLogs.findIndex(item => item.id === id);
        return index >= 2;
    };

    const getSortedDisplayItems = (items, lockedCheckFn) => {
        if (!items) return [];
        return [...items].sort((a, b) => {
            const lockedA = propertyLocked || lockedCheckFn(a.id);
            const lockedB = propertyLocked || lockedCheckFn(b.id);
            if (lockedA && !lockedB) return 1;
            if (!lockedA && lockedB) return -1;
            return new Date(b.created_at || b.date || 0) - new Date(a.created_at || a.date || 0);
        });
    };

    const displayAppliances = getSortedDisplayItems(appliances, isItemGloballyLocked);
    const displayFurniture = getSortedDisplayItems(furniture, isItemGloballyLocked);
    const displayPlants = getSortedDisplayItems(plants, isPlantLocked);
    const displayLogs = getSortedDisplayItems(serviceLogs, isLogLocked);

    const [isLogSheetOpen, setIsLogSheetOpen] = useState(false);
    const [showUpgradeSheet, setShowUpgradeSheet] = useState(false);
    const [isPaintEditOpen, setIsPaintEditOpen] = useState(false);
    const [isAddApplianceOpen, setIsAddApplianceOpen] = useState(false);
    const [isAddPaintOpen, setIsAddPaintOpen] = useState(false);
    const [isAddFurnitureOpen, setIsAddFurnitureOpen] = useState(false);
    const [isAddPlantOpen, setIsAddPlantOpen] = useState(false);
    const [selectedPaint, setSelectedPaint] = useState(null);
    const [editingInfo, setEditingInfo] = useState(null);
    const [editingPlant, setEditingPlant] = useState(null);
    const [editingAppliance, setEditingAppliance] = useState(null);
    const [editingFurniture, setEditingFurniture] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState(null);
    const location = useLocation();
    const [activePopup, setActivePopup] = useState(() => {
        const popupParam = new URLSearchParams(location.search).get("popup");
        return location.state?.activePopup || popupParam || null;
    });

    useEffect(() => {
        const popupParam = new URLSearchParams(location.search).get("popup");
        if (popupParam) {
            setActivePopup(popupParam);
        } else if (location.state?.activePopup) {
            setActivePopup(location.state.activePopup);
        }
    }, [location.search, location.state]);
    const [selectedApplianceFilter, setSelectedApplianceFilter] = useState("all");
    const [activeRoutineCategory, setActiveRoutineCategory] = useState(null);
    const [isCompletingTask, setIsCompletingTask] = useState(false);

    // Back button history synchronization for HomeRecords sheets/popups
    useModalHistory(!!activePopup, () => { setActivePopup(null); setActiveRoutineCategory(null); }, "records-popup");
    useModalHistory(!!activeRoutineCategory, () => setActiveRoutineCategory(null), "routine-category");
    useModalHistory(isLogSheetOpen, () => setIsLogSheetOpen(false), "log-sheet");
    useModalHistory(isAddApplianceOpen, () => setIsAddApplianceOpen(false), "add-appliance");
    useModalHistory(isAddFurnitureOpen, () => setIsAddFurnitureOpen(false), "add-furniture");
    useModalHistory(isAddPlantOpen, () => setIsAddPlantOpen(false), "add-plant");
    useModalHistory(isAddPaintOpen, () => setIsAddPaintOpen(false), "add-paint");
    useModalHistory(showUpgradeSheet, () => setShowUpgradeSheet(false), "upgrade-sheet");
    useModalHistory(!!editingAppliance, () => setEditingAppliance(null), "edit-appliance");
    useModalHistory(!!editingFurniture, () => setEditingFurniture(null), "edit-furniture");

    const ROUTINE_ICONS = {
        ChefHat, Shirt, Wind: WindIcon, Droplets, TreePine, Sofa
    };

    const handleMarkTaskDone = async (taskName) => {
        if (isCompletingTask) return;
        setIsCompletingTask(true);
        await addRoutineTask({
            item_id: null, // Generic task
            task_name: taskName,
            completed_at: new Date().toISOString()
        });
        setIsCompletingTask(false);
    };

    const getCustomFrequency = (taskName) => {
        const customInfo = quickInfo?.find(info => info.info_type === 'routine_interval' && info.label === taskName);
        return customInfo ? parseInt(customInfo.value, 10) : null;
    };

    const handleEditInterval = async (taskName, defaultFreq) => {
        if (propertyLocked) return setShowUpgradeSheet(true);
        
        const currentCustom = getCustomFrequency(taskName);
        const result = window.prompt(`Enter custom interval in days for "${taskName}"\n(Leave empty to restore default of ${defaultFreq}):`, currentCustom || defaultFreq);
        
        if (result !== null) {
            if (result.trim() === "") {
                const existing = quickInfo?.find(info => info.info_type === 'routine_interval' && info.label === taskName);
                if (existing) {
                    await deleteQuickInfo(existing.id);
                    toast({ title: "Restored to default interval." });
                }
            } else {
                const parsed = parseInt(result, 10);
                if (!isNaN(parsed) && parsed > 0) {
                    await upsertQuickInfo({
                        info_type: 'routine_interval',
                        label: taskName,
                        value: parsed.toString()
                    });
                    toast({ title: "Custom interval saved!" });
                } else {
                    toast({ title: "Invalid number", description: "Please enter a valid number of days.", variant: "destructive" });
                }
            }
        }
    };

    // Handle popup and sheet open/close side effects (scroll lock and hide nav)
    useEffect(() => {
        const anyOpen = activePopup || isLogSheetOpen || isPaintEditOpen || isAddApplianceOpen || isAddPaintOpen || isAddFurnitureOpen || isAddPlantOpen || editingPlant || editingInfo || deleteDialog?.open || showUpgradeSheet || editingAppliance || editingFurniture;
        if (anyOpen) {
            document.body.classList.add('popup-open');
            document.body.style.overflow = 'hidden';
        } else {
            document.body.classList.remove('popup-open');
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.classList.remove('popup-open');
            document.body.style.overflow = 'unset';
        };
    }, [activePopup, isLogSheetOpen, isPaintEditOpen, isAddApplianceOpen, isAddPaintOpen, isAddFurnitureOpen, isAddPlantOpen, editingPlant, editingInfo, deleteDialog?.open, showUpgradeSheet, editingAppliance, editingFurniture]);

    // Handlers
    const handleViewDetails = (appliance) => {
        navigate("/product-details", {
            state: {
                applianceId: appliance.id,
                item: {
                    name: appliance.name,
                    details: appliance.installation_date ? `Installed ${new Date(appliance.installation_date).getFullYear()}` : "No installation date",
                    merchant: appliance.brand || "Unknown",
                    warrantyExpiry: appliance.warranty_expiry,
                    nextServiceDate: appliance.next_service_date,
                }
            }
        });
    };

    const handleServiceLogClick = (log) => {
        const appliance = log.appliance_id
            ? appliances.find(a => a.id === log.appliance_id)
            : null;
        const logTitle = appliance ? `${appliance.brand || ''} ${appliance.name}`.trim() : 'Service Log';

        navigate("/maintenance-detail", {
            state: {
                serviceLogId: log.id,
                item: {
                    name: logTitle,
                    details: new Date(log.date).toLocaleDateString('en-ZA', { month: 'short', year: 'numeric' }),
                    purchaseDate: log.date,
                    cost: log.cost,
                    description: log.description,
                    invoiceUrl: log.invoice_url,
                }
            }
        });
    };

    const handleEditPaint = (paint) => {
        setSelectedPaint(paint);
        setIsPaintEditOpen(true);
    };

    const handleSavePaint = async (updatedPaint) => {
        if (selectedPaint?.id) {
            await updatePaintCode(selectedPaint.id, {
                room: updatedPaint.name,
                brand: updatedPaint.brand,
                code: updatedPaint.code,
                finish: updatedPaint.finish,
                lastPainted: updatedPaint.lastPainted,
            });
        }
    };

    const handleSaveAppliance = async (applianceData) => {
        if (!activeProperty?.id) {
            toast({
                title: "No Property Selected",
                description: "Please select or add a property first.",
                variant: "destructive",
            });
            return false;
        }
        try {
            if (editingAppliance) {
                const success = await updateAppliance(editingAppliance.id, applianceData);
                if (success) {
                    setEditingAppliance(null);
                    refetchAppliances();
                }
                return success;
            } else {
                return await addAppliance({ ...applianceData, propertyId: activeProperty.id });
            }
        } catch (error) {
            console.error("Failed to save appliance:", error);
            return false;
        }
    };

    const handleSaveFurniture = async (furnitureData) => {
        if (!activeProperty?.id) {
            toast({
                title: "No Property Selected",
                description: "Please select or add a property first.",
                variant: "destructive",
            });
            return false;
        }
        try {
            if (editingFurniture) {
                const success = await updateFurniture(editingFurniture.id, furnitureData);
                if (success) {
                    setEditingFurniture(null);
                    refetchFurniture();
                }
                return success;
            }
            return false;
        } catch (error) {
            console.error("Failed to save furniture:", error);
            return false;
        }
    };

    const handleAddPaint = async (paint) => {
        const success = await addPaintCode({ ...paint, propertyId: activeProperty?.id });
        if (success) {
            toast({
                title: "Paint Code Added",
                description: `${paint.room} paint code has been saved.`,
            });
        }
        return success;
    };

    const handleEditQuickInfo = (item) => {
        setEditingInfo({
            infoType: item.info_type,
            label: item.label,
            value: item.value,
            subValue: item.sub_value,
            isCustom: !["water_shutoff", "db_board"].includes(item.info_type)
        });
    };

    const handleAddQuickInfo = () => {
        setEditingInfo({
            isCustom: true,
            infoType: null,
            label: "",
            value: "",
            subValue: ""
        });
    };

    const handleSaveQuickInfo = async (value, subValue, customLabel) => {
        if (editingInfo) {
            let type = editingInfo.infoType;
            let label = editingInfo.label;

            if (editingInfo.isCustom && customLabel) {
                label = customLabel;
                // Generate a crude ID if new
                if (!type) {
                    type = customLabel.toLowerCase().replace(/[^a-z0-9]/g, "_");
                }
            }

            await upsertQuickInfo(type, label, value, subValue);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteDialog) return;

        let success = false;
        if (deleteDialog.type === "appliance") {
            success = await deleteAppliance(deleteDialog.id);
        } else if (deleteDialog.type === "paint") {
            success = await deletePaintCode(deleteDialog.id);
        } else if (deleteDialog.type === "service") {
            success = await deleteServiceLog(deleteDialog.id);
        } else if (deleteDialog.type === "quick_info") {
            success = await deleteQuickInfo(deleteDialog.id);
        } else if (deleteDialog.type === "furniture") {
            success = await deleteFurniture(deleteDialog.id);
        } else if (deleteDialog.type === "plant") {
            success = await deletePlant(deleteDialog.id);
        }

        if (success) {
            setDeleteDialog(null);
        }
    };

    const isLoading = appliancesLoading || paintLoading || logsLoading || quickInfoLoading || furnitureLoading || plantsLoading;

    const getQuickInfoIcon = (type) => {
        switch (type) {
            case "water_shutoff": return Droplets;
            case "db_board": return Zap;
            case "wifi": return Loader2;
            default: return FileText;
        }
    };

    const speedDialOptions = [
        {
            id: "appliance",
            label: "Add Appliance",
            icon: Refrigerator,
            onClick: () => setIsAddApplianceOpen(true),
        },
        {
            id: "paint",
            label: "Add Paint Code",
            icon: Paintbrush,
            onClick: () => setIsAddPaintOpen(true),
        },
        {
            id: "furniture",
            label: "Add Furniture",
            icon: Sofa,
            onClick: () => setIsAddFurnitureOpen(true),
        },
        {
            id: "plant",
            label: "Add Plant",
            icon: Sprout,
            onClick: () => setIsAddPlantOpen(true),
        },
        {
            id: "quick_info",
            label: "Add General Info",
            icon: Info,
            onClick: handleAddQuickInfo,
        },
        {
            id: "maintenance",
            label: "Add a Service Log",
            icon: Wrench,
            onClick: () => setIsLogSheetOpen(true),
        },
    ];

    const handleSpeedDialClick = (onClick) => {
        if (propertyLocked) {
            setShowUpgradeSheet(true);
        } else {
            onClick();
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-[hsl(var(--gradient-start))] to-[hsl(var(--gradient-end))] pb-24">
            <PageHeader pageName="HomeEzi" houseName={activeProperty?.name} />

            <main className="px-6" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 7rem)" }}>
                <div className="max-w-lg mx-auto">
                    {isLoading ? (
                        <div className="flex justify-center py-12">
                            <Loader2 className="w-8 h-8 text-primary animate-spin" />
                        </div>
                    ) : propertyLocked ? (
                        <GlassCard className="p-8 text-center flex flex-col items-center justify-center space-y-6 border border-amber-500/20 bg-amber-500/5 backdrop-blur-md">
                            <div className="w-16 h-16 bg-amber-500/10 rounded-full flex items-center justify-center mb-2">
                                <Lock className="w-8 h-8 text-amber-600" />
                            </div>
                            <div className="space-y-2">
                                <h3 className="text-xl font-bold text-foreground">Property Locked</h3>
                                <p className="text-sm text-muted-foreground leading-relaxed max-w-sm">
                                    Your plan allows managing up to 1 house. Please upgrade your subscription to access this property.
                                </p>
                            </div>
                            <div className="flex flex-col w-full gap-3">
                                <Button onClick={() => setShowUpgradeSheet(true)} className="w-full rounded-2xl py-6 font-bold bg-primary shadow-lg shadow-primary/20">
                                    Upgrade Plan
                                </Button>
                                {(() => {
                                    const firstUnlocked = properties.find(p => !isPropertyLocked(p.id));
                                    if (firstUnlocked) {
                                        return (
                                            <Button 
                                                variant="outline"
                                                onClick={async () => {
                                                    await setActiveProperty(firstUnlocked.id);
                                                    if (typeof window !== 'undefined') window.location.reload();
                                                }}
                                                className="w-full rounded-2xl py-6 font-semibold"
                                            >
                                                Switch to {firstUnlocked.name}
                                            </Button>
                                        );
                                    }
                                    return null;
                                })()}
                            </div>
                        </GlassCard>
                    ) : (
                        <>
                            
                            {/* Grid Layout Redesign */}
                            <div className="grid grid-cols-2 gap-4">
                                {/* Row 1: General (Centered, span 2) */}
                                <div className="col-span-2 flex justify-center">
                                    <CategoryIcon 
                                        icon={Home} 
                                        label="General Info"
                                        id="general"
                                        onClick={() => setActivePopup('general')}
                                        locked={false}
                                    />
                                </div>

                                {/* Row 2: Appliances, Furniture */}
                                <CategoryIcon icon={Refrigerator} label="Appliances" id="appliances" onClick={() => setActivePopup('appliances')} color="text-blue-600" bg="bg-blue-500/10" />
                                <CategoryIcon icon={Sofa} label="Furniture" id="furniture" onClick={() => setActivePopup('furniture')} color="text-purple-600" bg="bg-purple-500/10" />

                                {/* Row 3: Plants, Paint */}
                                <CategoryIcon icon={Sprout} label="Plants" id="plants" onClick={() => setActivePopup('plants')} color="text-emerald-600" bg="bg-emerald-500/10" />
                                <CategoryIcon icon={Paintbrush} label="Paint Codes" id="paint" onClick={() => setActivePopup('paint')} color="text-pink-600" bg="bg-pink-500/10" />

                                {/* Row 4: Service & Routine */}
                                <CategoryIcon 
                                    icon={Wrench} 
                                    label="Service History" 
                                    id="service"
                                    onClick={() => setActivePopup('service')}
                                    locked={isFeatureLocked('maintenance_history')}
                                    color="text-orange-600" bg="bg-orange-500/10"
                                />
                                <CategoryIcon 
                                    icon={CheckCircle2} 
                                    label="Routine Care" 
                                    id="routine"
                                    onClick={() => setActivePopup('routine')}
                                    color="text-amber-600" bg="bg-amber-500/10"
                                />
                            </div>

                        </>
                    )}
                </div>
            
                    

            </main>

            {/* Floating Popups */}
            <AnimatePresence>

                {activePopup === 'routine' && (
                    <div 
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
                        style={{
                            paddingTop: "calc(env(safe-area-inset-top, 0px) + 1rem)",
                            paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)"
                        }}
                    >
                        <motion.div
                            key="popup-routine"
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            transition={{ type: "spring", damping: 25, stiffness: 300 }}
                            className="w-full h-[90vh] max-w-2xl bg-white/95 backdrop-blur-xl border border-white/20 shadow-2xl rounded-3xl flex flex-col overflow-hidden pointer-events-auto"
                            style={{ maxHeight: "100%" }}
                        >
                            <div className="flex justify-between items-center p-6 border-b border-primary/10">
                                <div className="flex items-center gap-3">
                                    {activeRoutineCategory && (
                                        <Button variant="ghost" size="icon" onClick={() => setActiveRoutineCategory(null)} className="rounded-full bg-slate-100 hover:bg-slate-200">
                                            <ChevronLeft className="w-5 h-5" />
                                        </Button>
                                    )}
                                    <h2 className="text-2xl font-bold text-foreground">
                                        {activeRoutineCategory ? activeRoutineCategory.name : "Routine Care"}
                                    </h2>
                                </div>
                                <Button variant="ghost" size="icon" onClick={() => { setActivePopup(null); setActiveRoutineCategory(null); }} className="rounded-full bg-slate-100 hover:bg-slate-200">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x w-5 h-5"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                                </Button>
                            </div>
                            <div className="flex-1 overflow-y-auto p-6 relative">
                                {!activeRoutineCategory ? (
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                        {ROUTINE_CATEGORIES.map(category => {
                                            const IconComponent = ROUTINE_ICONS[category.iconName] || CheckCircle2;

                                            return (
                                                <div 
                                                    key={category.id}
                                                    onClick={() => setActiveRoutineCategory(category)}
                                                    className="flex flex-col items-center justify-center p-6 bg-white/50 backdrop-blur-md border border-white/20 rounded-3xl shadow-sm hover:shadow-md transition-all cursor-pointer active:scale-95 text-center relative"
                                                >
                                                    <div className="p-4 rounded-2xl mb-3 bg-amber-500/10">
                                                        <IconComponent className="w-8 h-8 text-amber-600" />
                                                    </div>
                                                    <span className="font-semibold text-foreground text-sm">{category.name}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="space-y-6">
                                        {(() => {
                                            const todoTasks = [];
                                            const doneTasks = [];
                                            
                                            activeRoutineCategory.tasks.forEach(task => {
                                                const customFreq = getCustomFrequency(task.name);
                                                const effectiveFreq = customFreq || task.frequency_days;
                                                const lastDone = (routineTasks || []).find(t => t.task_name === task.name);
                                                const isOverdue = lastDone ? (new Date() - new Date(lastDone.completed_at)) > (effectiveFreq * 24 * 60 * 60 * 1000) : true;
                                                
                                                const taskData = { ...task, lastDone, effectiveFreq, isCustom: !!customFreq };
                                                if (isOverdue) {
                                                    todoTasks.push(taskData);
                                                } else {
                                                    doneTasks.push(taskData);
                                                }
                                            });

                                            return (
                                                <>
                                                    {todoTasks.length > 0 && (
                                                        <div>
                                                            <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider mb-3">To Do / Recommended</h3>
                                                            <div className="space-y-3">
                                                                {todoTasks.map(task => (
                                                                    <div key={task.id} className="bg-white/60 backdrop-blur-md border border-amber-500/20 rounded-2xl p-4 shadow-sm relative overflow-hidden">
                                                                        <div className="flex justify-between items-start mb-2">
                                                                            <h4 className="font-bold text-sm text-foreground pr-2">{task.name}</h4>
                                                                            <Button 
                                                                                size="sm" 
                                                                                className="h-7 px-3 text-xs rounded-full bg-primary text-white shrink-0"
                                                                                onClick={() => handleMarkTaskDone(task.name)}
                                                                                disabled={isCompletingTask}
                                                                            >
                                                                                Mark Done
                                                                            </Button>
                                                                        </div>
                                                                        <p className="text-xs text-muted-foreground mb-3">{task.description}</p>
                                                                        
                                                                        <div className="flex items-center gap-2">
                                                                            {task.lastDone ? (
                                                                                <span className="text-[10px] font-bold text-red-600 bg-red-50 px-2 py-1 rounded-full uppercase tracking-wider">Overdue</span>
                                                                            ) : (
                                                                                <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-1 rounded-full uppercase tracking-wider">Never Done</span>
                                                                            )}
                                                                            <div 
                                                                                onClick={() => handleEditInterval(task.name, task.frequency_days)}
                                                                                className="flex items-center gap-1 text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-1 rounded-full uppercase tracking-wider cursor-pointer hover:bg-slate-200 transition-colors"
                                                                            >
                                                                                Every {task.effectiveFreq} days {task.isCustom && "(Custom)"}
                                                                                <Edit2 className="w-3 h-3 ml-0.5" />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {doneTasks.length > 0 && (
                                                        <div>
                                                            <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider mb-3">Done (Up to date)</h3>
                                                            <div className="space-y-3">
                                                                {doneTasks.map(task => (
                                                                    <div key={task.id} className="bg-emerald-50/50 backdrop-blur-md border border-emerald-500/20 rounded-2xl p-4 shadow-sm relative overflow-hidden opacity-75">
                                                                        <div className="flex justify-between items-start mb-2">
                                                                            <div className="flex items-center gap-2">
                                                                                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                                                                                <h4 className="font-bold text-sm text-foreground line-through decoration-emerald-600/30">{task.name}</h4>
                                                                            </div>
                                                                        </div>
                                                                        <p className="text-xs text-muted-foreground mb-3">{task.description}</p>
                                                                        
                                                                        <div className="flex flex-wrap items-center gap-2">
                                                                            <span className="text-[10px] font-bold text-emerald-600 bg-emerald-100/50 px-2 py-1 rounded-full uppercase tracking-wider">
                                                                                Done {new Date(task.lastDone.completed_at).toLocaleDateString('en-ZA', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                                            </span>
                                                                            <div 
                                                                                onClick={() => handleEditInterval(task.name, task.frequency_days)}
                                                                                className="flex items-center gap-1 text-[10px] font-bold text-emerald-700/70 bg-emerald-100/50 px-2 py-1 rounded-full uppercase tracking-wider cursor-pointer hover:bg-emerald-200/50 transition-colors"
                                                                            >
                                                                                Every {task.effectiveFreq} days {task.isCustom && "(Custom)"}
                                                                                <Edit2 className="w-3 h-3 ml-0.5" />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                    
                                                    {todoTasks.length === 0 && doneTasks.length === 0 && (
                                                        <p className="text-sm text-muted-foreground">No tasks available for this category.</p>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </div>
                                )}
                            </div>
                        </motion.div>
                    </div>
                )}

                {activePopup && (
                    <motion.div
                        key="overlay"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setActivePopup(null)}
                        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
                    />
                )}
                
                
                {activePopup === 'general' && (
                    <div 
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
                        style={{
                            paddingTop: "calc(env(safe-area-inset-top, 0px) + 1rem)",
                            paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)"
                        }}
                    >
                        <motion.div
                            key="popup-general"
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            transition={{ type: "spring", damping: 25, stiffness: 300 }}
                            className="w-full h-[90vh] max-w-2xl bg-white/95 backdrop-blur-xl border border-white/20 shadow-2xl rounded-3xl flex flex-col overflow-hidden pointer-events-auto"
                            style={{ maxHeight: "100%" }}
                        >
                        <div className="flex justify-between items-center p-6 border-b border-primary/10">
                            <h2 className="text-2xl font-bold text-foreground">General Info</h2>
                            <div className="flex items-center gap-2">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                        if (propertyLocked) return setShowUpgradeSheet(true);
                                        handleAddQuickInfo();
                                    }}
                                    className="rounded-full font-semibold text-xs flex items-center gap-1.5 h-9 bg-primary/5 hover:bg-primary text-primary hover:text-white border-primary/10 hover:border-transparent transition-all"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-plus w-3.5 h-3.5"><path d="M5 12h14"/><path d="M12 5v14"/></svg>
                                    Add Info
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => setActivePopup(null)} className="rounded-full bg-slate-100 hover:bg-slate-200">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x w-5 h-5"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                                </Button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 relative">
                            <div className="space-y-3">
                                    {quickInfo.length === 0 ? (
                                        <GlassCard className="p-8">
                                            <div className="text-center">
                                                <Info className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                                                <p className="text-sm font-medium text-foreground">No info added</p>
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    Add key details like Water Shut-off or WiFi.
                                                </p>
                                            </div>
                                        </GlassCard>
                                    ) : (
                                        quickInfo.map((info, index) => {
                                            const Icon = getQuickInfoIcon(info.info_type);
                                            return (
                                                <SwipeableCard
                                                    key={info.id}
                                                    onDelete={() => {
                                                        if (propertyLocked) return setShowUpgradeSheet(true);
                                                        setDeleteDialog({
                                                            open: true,
                                                            type: "quick_info",
                                                            id: info.id,
                                                            name: info.label,
                                                        });
                                                    }}
                                                    onClick={() => propertyLocked ? setShowUpgradeSheet(true) : handleEditQuickInfo(info)}
                                                    className="active-scale transition-transform cursor-pointer home-card-entry"
                                                    style={{ animationDelay: `${index * 50}ms` }}
                                                >
                                                    <GlassCard className={cn("p-4 transition-all hover:home-glow", propertyLocked && "blur-[2px] opacity-70 grayscale pointer-events-none select-none")}>
                                                        <div className="flex items-start gap-4">
                                                            <div className="p-2 rounded-xl bg-indigo-500/10 shrink-0">
                                                                <Icon className="w-5 h-5 text-indigo-600" />
                                                            </div>
                                                            <div className="flex-1 min-w-0 text-left">
                                                                <h3 className="font-bold text-foreground">{info.label}</h3>

                                                                <div className="flex flex-wrap gap-2 mt-2">
                                                                    <div className="flex items-center gap-1 text-[10px] bg-slate-500/10 text-slate-600 px-2 py-0.5 rounded-full border border-slate-500/20">
                                                                        <span className="font-semibold select-all">{info.value}</span>
                                                                    </div>
                                                                </div>

                                                                {info.sub_value && (
                                                                    <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-indigo-500/10 font-medium">
                                                                        {info.sub_value}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </GlassCard>
                                                </SwipeableCard>
                                            );
                                        })
                                    )}
                                </div>
                            
                        </div>
                    </motion.div>
                    </div>
                )}

                
                {activePopup === 'appliances' && (
                    <div 
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
                        style={{
                            paddingTop: "calc(env(safe-area-inset-top, 0px) + 1rem)",
                            paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)"
                        }}
                    >
                        <motion.div
                            key="popup-appliances"
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            transition={{ type: "spring", damping: 25, stiffness: 300 }}
                            className="w-full h-[90vh] max-w-2xl bg-white/95 backdrop-blur-xl border border-white/20 shadow-2xl rounded-3xl flex flex-col overflow-hidden pointer-events-auto"
                            style={{ maxHeight: "100%" }}
                        >
                        <div className="flex justify-between items-center p-6 border-b border-primary/10">
                            <h2 className="text-2xl font-bold text-foreground">Appliances</h2>
                            <div className="flex gap-2">
                                <Button size="sm" onClick={() => setIsAddApplianceOpen(true)} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-4">
                                    <Plus className="w-4 h-4 mr-1.5" /> Add
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => setActivePopup(null)} className="rounded-full bg-slate-100 hover:bg-slate-200">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x w-5 h-5"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                                </Button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 relative">
                            <div className="space-y-3">
                                    {appliances.length === 0 ? (
                                        <GlassCard className="p-8">
                                            <div className="text-center">
                                                <Refrigerator className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                                                <p className="text-sm font-medium text-foreground">No appliances found</p>
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    Add your first appliance to start tracking warranties.
                                                </p>
                                            </div>
                                        </GlassCard>
                                    ) : (
                                        displayAppliances
                                            .map((appliance, index) => {
                                                const isLocked = propertyLocked || isItemGloballyLocked(appliance.id);
                                                return (
                                                <SwipeableCard
                                                    key={appliance.id}
                                                    onDelete={() => {
                                                        if (isLocked) return setShowUpgradeSheet(true);
                                                        setDeleteDialog({
                                                        open: true,
                                                        type: "appliance",
                                                        id: appliance.id,
                                                        name: `${appliance.brand || ""} ${appliance.name}`.trim(),
                                                    })}}
                                                    onClick={() => isLocked ? setShowUpgradeSheet(true) : setEditingAppliance(appliance)}
                                                    className="active-scale transition-transform cursor-pointer home-card-entry"
                                                    style={{ animationDelay: `${index * 50}ms` }}
                                                >
                                                    <GlassCard className={cn("p-4 transition-all relative overflow-hidden hover:home-glow", isLocked && "blur-[2px] opacity-70 grayscale pointer-events-none select-none")}>
                                                        <div className="flex items-start gap-4">
                                                            <div className="p-2 rounded-xl bg-blue-500/10 shrink-0">
                                                                <Refrigerator className="w-5 h-5 text-blue-600" />
                                                            </div>
                                                            <div className="flex-1 min-w-0 text-left">
                                                                <h3 className="font-bold text-foreground">{appliance.name}</h3>

                                                                <div className="flex flex-wrap gap-2 mt-2">
                                                                    {appliance.brand && (
                                                                        <div className="flex items-center gap-1 text-[10px] bg-slate-500/10 text-slate-600 px-2 py-0.5 rounded-full border border-slate-500/20">
                                                                            <span className="font-semibold">{appliance.brand}</span>
                                                                        </div>
                                                                    )}
                                                                    {appliance.warranty_expiry && (
                                                                        <div className="flex items-center gap-1 text-[10px] bg-amber-500/10 text-amber-600 px-2 py-0.5 rounded-full border border-amber-500/20">
                                                                            <Calendar className="w-3 h-3" />
                                                                            Expires {new Date(appliance.warranty_expiry).toLocaleDateString()}
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                <div className="mt-3 pt-3 border-t border-blue-500/10 flex items-center justify-between">
                                                                    <div className="flex flex-col">
                                                                        <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">
                                                                            {appliance.next_service_date ? "Next Service" : "Installed"}
                                                                        </span>
                                                                        <span className={cn(
                                                                            "text-xs font-bold",
                                                                            appliance.next_service_date && new Date(appliance.next_service_date) < new Date() ? "text-destructive" : "text-blue-600"
                                                                        )}>
                                                                            {appliance.next_service_date
                                                                                ? new Date(appliance.next_service_date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })
                                                                                : (appliance.installation_date ? new Date(appliance.installation_date).toLocaleDateString('en-ZA', { year: 'numeric' }) : "N/A")
                                                                            }
                                                                        </span>
                                                                    </div>
                                                                    {appliance.manual_url && (
                                                                        <span className="inline-flex items-center gap-1 text-[10px] bg-blue-500/10 text-blue-600 px-2 py-1 rounded-md font-medium">
                                                                            <FileText className="w-3 h-3" /> Manual
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                {appliance.notes && (
                                                                    <p className="text-xs text-muted-foreground mt-2 line-clamp-2 italic font-medium">
                                                                        "{appliance.notes}"
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </GlassCard>
                                                    {isLocked && (
                                                        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-auto" onClick={(e) => { e.stopPropagation(); setShowUpgradeSheet(true); }}>
                                                            <div className="bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-md border border-primary/20 flex items-center gap-1.5">
                                                                <Lock className="w-3 h-3 text-primary" />
                                                                <span className="text-[10px] font-bold text-primary uppercase">Locked</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </SwipeableCard>
                                            )})
                                    )}
                                </div>
                            
                        </div>
                    </motion.div>
                    </div>
                )}

                
                {activePopup === 'furniture' && (
                    <div 
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
                        style={{
                            paddingTop: "calc(env(safe-area-inset-top, 0px) + 1rem)",
                            paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)"
                        }}
                    >
                        <motion.div
                            key="popup-furniture"
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            transition={{ type: "spring", damping: 25, stiffness: 300 }}
                            className="w-full h-[90vh] max-w-2xl bg-white/95 backdrop-blur-xl border border-white/20 shadow-2xl rounded-3xl flex flex-col overflow-hidden pointer-events-auto"
                            style={{ maxHeight: "100%" }}
                        >
                        <div className="flex justify-between items-center p-6 border-b border-primary/10">
                            <h2 className="text-2xl font-bold text-foreground">Furniture</h2>
                            <div className="flex gap-2">
                                <Button size="sm" onClick={() => setIsAddFurnitureOpen(true)} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-4">
                                    <Plus className="w-4 h-4 mr-1.5" /> Add
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => setActivePopup(null)} className="rounded-full bg-slate-100 hover:bg-slate-200">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x w-5 h-5"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                                </Button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 relative">
                            <div className="space-y-3">
                                    {furniture.length === 0 ? (
                                        <GlassCard className="p-8">
                                            <div className="text-center">
                                                <Sofa className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                                                <p className="text-sm font-medium text-foreground">No furniture added</p>
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    Track items like couches, tables, and more.
                                                </p>
                                            </div>
                                        </GlassCard>
                                    ) : (
                                        displayFurniture
                                            .map((item, index) => {
                                                const isLocked = propertyLocked || isItemGloballyLocked(item.id);
                                                return (
                                                <SwipeableCard
                                                    key={item.id}
                                                    onDelete={() => {
                                                        if (isLocked) return setShowUpgradeSheet(true);
                                                        setDeleteDialog({
                                                        open: true,
                                                        type: "furniture",
                                                        id: item.id,
                                                        name: item.name,
                                                    })}}
                                                    onClick={() => {
                                                        if (isLocked) return setShowUpgradeSheet(true);
                                                        setEditingFurniture(item);
                                                    }}
                                                    className="active-scale transition-transform cursor-pointer home-card-entry"
                                                    style={{ animationDelay: `${index * 50}ms` }}
                                                >
                                                    <GlassCard className={cn("p-4 relative overflow-hidden transition-all hover:home-glow", isLocked && "blur-[2px] opacity-70 grayscale pointer-events-none select-none")}>
                                                        <div className="flex items-start gap-4">
                                                            <div className="p-2 rounded-xl bg-purple-500/10 shrink-0">
                                                                <Sofa className="w-5 h-5 text-purple-600" />
                                                            </div>
                                                            <div className="flex-1 min-w-0 text-left">
                                                                <h3 className="font-bold text-foreground">{item.name}</h3>

                                                                <div className="flex flex-wrap gap-2 mt-2">
                                                                    {item.brand && (
                                                                        <div className="flex items-center gap-1 text-[10px] bg-slate-500/10 text-slate-600 px-2 py-0.5 rounded-full border border-slate-500/20">
                                                                            <span className="font-semibold">{item.brand}</span>
                                                                        </div>
                                                                    )}
                                                                    {item.warranty_expiry && (
                                                                        <div className="flex items-center gap-1 text-[10px] bg-amber-500/10 text-amber-600 px-2 py-0.5 rounded-full border border-amber-500/20">
                                                                            <Calendar className="w-3 h-3" />
                                                                            Expires {new Date(item.warranty_expiry).toLocaleDateString()}
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                <div className="mt-3 pt-3 border-t border-purple-500/10 flex items-center justify-between">
                                                                    <div className="flex flex-col">
                                                                        <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">Added</span>
                                                                        <span className="text-xs font-bold text-purple-600">
                                                                            {new Date(item.created_at).getFullYear()}
                                                                        </span>
                                                                    </div>
                                                                    <div className="flex gap-2">
                                                                        {item.invoice_url && (
                                                                            <a href={item.invoice_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[10px] bg-purple-500/10 text-purple-600 px-2 py-1 rounded-md font-medium">
                                                                                <FileText className="w-3 h-3" /> Invoice
                                                                            </a>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                {item.notes && (
                                                                    <p className="text-xs text-muted-foreground mt-2 line-clamp-2 italic font-medium">
                                                                        "{item.notes}"
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </GlassCard>
                                                    {isLocked && (
                                                        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-auto" onClick={(e) => { e.stopPropagation(); setShowUpgradeSheet(true); }}>
                                                            <div className="bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-md border border-primary/20 flex items-center gap-1.5">
                                                                <Lock className="w-3 h-3 text-primary" />
                                                                <span className="text-[10px] font-bold text-primary uppercase">Locked</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </SwipeableCard>
                                            )})
                                    )}
                                </div>
                            
                        </div>
                    </motion.div>
                    </div>
                )}

                
                {activePopup === 'plants' && (
                    <div 
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
                        style={{
                            paddingTop: "calc(env(safe-area-inset-top, 0px) + 1rem)",
                            paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)"
                        }}
                    >
                        <motion.div
                            key="popup-plants"
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            transition={{ type: "spring", damping: 25, stiffness: 300 }}
                            className="w-full h-[90vh] max-w-2xl bg-white/95 backdrop-blur-xl border border-white/20 shadow-2xl rounded-3xl flex flex-col overflow-hidden pointer-events-auto"
                            style={{ maxHeight: "100%" }}
                        >
                        <div className="flex justify-between items-center p-6 border-b border-primary/10">
                            <h2 className="text-2xl font-bold text-foreground">Plants</h2>
                            <div className="flex gap-2">
                                <Button size="sm" onClick={() => setIsAddPlantOpen(true)} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-4">
                                    <Plus className="w-4 h-4 mr-1.5" /> Add
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => setActivePopup(null)} className="rounded-full bg-slate-100 hover:bg-slate-200">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x w-5 h-5"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                                </Button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 relative">
                            <div className="space-y-3">
                                    {plants.length === 0 ? (
                                        <GlassCard className="p-8">
                                            <div className="text-center">
                                                <Sprout className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                                                <p className="text-sm font-medium text-foreground">No plants added</p>
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    Keep your home green and healthy.
                                                </p>
                                            </div>
                                        </GlassCard>
                                    ) : (
                                        displayPlants
                                            .map((plant, index) => {
                                                const isLocked = propertyLocked || isPlantLocked(plant.id);
                                                return (
                                                <SwipeableCard
                                                    key={plant.id}
                                                    onDelete={() => {
                                                        if (isLocked) return setShowUpgradeSheet(true);
                                                        setDeleteDialog({
                                                        open: true,
                                                        type: "plant",
                                                        id: plant.id,
                                                        name: plant.name,
                                                    })}}
                                                    onClick={() => isLocked ? setShowUpgradeSheet(true) : setEditingPlant(plant)}
                                                    className="active-scale transition-transform cursor-pointer home-card-entry"
                                                    style={{ animationDelay: `${index * 50}ms` }}
                                                >
                                                    <GlassCard className={cn("p-4 relative overflow-hidden transition-all hover:home-glow", isLocked && "blur-[2px] opacity-70 grayscale pointer-events-none select-none")}>
                                                        <div className="flex items-start gap-4">
                                                            <div className="p-2 rounded-xl bg-emerald-500/10 shrink-0">
                                                                <Sprout className="w-5 h-5 text-emerald-600" />
                                                            </div>
                                                            <div className="flex-1 min-w-0 text-left">
                                                                <div className="flex justify-between items-start">
                                                                    <div className="flex items-center gap-2">
                                                                        <h3 className="font-bold text-foreground">{plant.name}</h3>
                                                                        {plant.is_paused && (
                                                                            <span className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 text-[10px] font-bold px-2 py-0.5 rounded-full border border-amber-200 dark:border-amber-800/30 uppercase tracking-wider">
                                                                                Paused
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <div className="flex flex-wrap gap-2 mt-2">
                                                                    <div className="flex items-center gap-1 text-[10px] bg-blue-500/10 text-blue-600 px-2 py-0.5 rounded-full border border-blue-500/20">
                                                                        <Droplets className="w-3 h-3" />
                                                                        Every {plant.watering_frequency} {plant.watering_frequency === 1 ? plant.watering_unit.slice(0, -1) : plant.watering_unit}
                                                                    </div>
                                                                    <div className="flex items-center gap-1 text-[10px] bg-amber-500/10 text-amber-600 px-2 py-0.5 rounded-full border border-amber-500/20 capitalize">
                                                                        <Sun className="w-3 h-3" />
                                                                        {plant.sunlight_preference} Light
                                                                    </div>
                                                                </div>

                                                                <div className="mt-3 pt-3 border-t border-emerald-500/10 flex items-center justify-between">
                                                                    <div className="flex flex-col">
                                                                        <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">Next Water</span>
                                                                        <span className={cn(
                                                                            "text-xs font-bold",
                                                                            (() => {
                                                                                const today = new Date();
                                                                                today.setHours(0, 0, 0, 0);
                                                                                const nextWater = new Date(plant.next_watering_at);
                                                                                nextWater.setHours(0, 0, 0, 0);
                                                                                return nextWater <= today ? "text-red-500" : "text-emerald-600";
                                                                            })()
                                                                        )}>
                                                                            {new Date(plant.next_watering_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}
                                                                        </span>
                                                                    </div>
                                                                    <Button
                                                                        size="sm"
                                                                        variant="ghost"
                                                                        className={cn(
                                                                            "h-8 rounded-lg transition-all text-[10px] font-bold",
                                                                            (() => {
                                                                                const today = new Date();
                                                                                today.setHours(0, 0, 0, 0);
                                                                                const nextWater = new Date(plant.next_watering_at);
                                                                                nextWater.setHours(0, 0, 0, 0);
                                                                                return nextWater <= today
                                                                                    ? "bg-red-500/10 text-red-500 hover:bg-emerald-500 hover:text-white"
                                                                                    : "bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500 hover:text-white";
                                                                            })()
                                                                        )}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            markAsWatered(plant.id);
                                                                        }}
                                                                    >
                                                                        Mark Watered
                                                                    </Button>
                                                                </div>

                                                                {plant.notes && (
                                                                    <p className="text-xs text-muted-foreground mt-2 line-clamp-2 italic font-medium">
                                                                        "{plant.notes}"
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </GlassCard>
                                                    {isLocked && (
                                                        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-auto" onClick={(e) => { e.stopPropagation(); setShowUpgradeSheet(true); }}>
                                                            <div className="bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-md border border-primary/20 flex items-center gap-1.5">
                                                                <Lock className="w-3 h-3 text-primary" />
                                                                <span className="text-[10px] font-bold text-primary uppercase">Locked</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </SwipeableCard>
                                            )})
                                    )}
                                </div>
                            
                        </div>
                    </motion.div>
                    </div>
                )}

                
                {activePopup === 'paint' && (
                    <div 
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
                        style={{
                            paddingTop: "calc(env(safe-area-inset-top, 0px) + 1rem)",
                            paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)"
                        }}
                    >
                        <motion.div
                            key="popup-paint"
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            transition={{ type: "spring", damping: 25, stiffness: 300 }}
                            className="w-full h-[90vh] max-w-2xl bg-white/95 backdrop-blur-xl border border-white/20 shadow-2xl rounded-3xl flex flex-col overflow-hidden pointer-events-auto"
                            style={{ maxHeight: "100%" }}
                        >
                        <div className="flex justify-between items-center p-6 border-b border-primary/10">
                            <h2 className="text-2xl font-bold text-foreground">Paint Codes</h2>
                            <div className="flex gap-2">
                                <Button size="sm" onClick={() => setIsAddPaintOpen(true)} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-4">
                                    <Plus className="w-4 h-4 mr-1.5" /> Add
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => setActivePopup(null)} className="rounded-full bg-slate-100 hover:bg-slate-200">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x w-5 h-5"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                                </Button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 relative">
                            <div className="space-y-3">
                                    {paintCodes.length === 0 ? (
                                        <GlassCard className="p-8">
                                            <div className="text-center">
                                                <Paintbrush className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
                                                <p className="text-sm font-medium text-foreground">No paint codes yet</p>
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    Keep track of your wall colors here.
                                                </p>
                                            </div>
                                        </GlassCard>
                                    ) : (
                                        paintCodes
                                            .map((paint, index) => (
                                                <SwipeableCard
                                                    key={paint.id}
                                                    onDelete={() => setDeleteDialog({
                                                        open: true,
                                                        type: "paint",
                                                        id: paint.id,
                                                        name: paint.room,
                                                    })}
                                                    onClick={() => handleEditPaint({
                                                        id: paint.id,
                                                        name: paint.room,
                                                        brand: paint.brand || "",
                                                        code: paint.code || "",
                                                        finish: paint.finish || "",
                                                        lastPainted: paint.last_painted || undefined,
                                                    })}
                                                    className="active-scale transition-transform cursor-pointer home-card-entry"
                                                    style={{ animationDelay: `${index * 50}ms` }}
                                                >
                                                    <GlassCard className="p-4 transition-all hover:home-glow">
                                                        <div className="flex items-start gap-4">
                                                            <div className="p-2 rounded-xl bg-pink-500/10 shrink-0">
                                                                <Paintbrush className="w-5 h-5 text-pink-600" />
                                                            </div>
                                                            <div className="flex-1 min-w-0 text-left">
                                                                <h3 className="font-bold text-foreground">{paint.room}</h3>

                                                                <div className="flex flex-wrap gap-2 mt-2">
                                                                    {paint.brand && (
                                                                        <div className="flex items-center gap-1 text-[10px] bg-slate-500/10 text-slate-600 px-2 py-0.5 rounded-full border border-slate-500/20">
                                                                            <span className="font-semibold">{paint.brand}</span>
                                                                        </div>
                                                                    )}
                                                                    {paint.finish && (
                                                                        <div className="flex items-center gap-1 text-[10px] bg-pink-500/10 text-pink-600 px-2 py-0.5 rounded-full border border-pink-500/20 capitalize">
                                                                            <Droplets className="w-3 h-3" />
                                                                            {paint.finish}
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                <div className="mt-3 pt-3 border-t border-pink-500/10 flex items-center justify-between">
                                                                    <div className="flex flex-col">
                                                                        <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">Color Code</span>
                                                                        <span className="text-xs font-bold text-pink-600 select-all">
                                                                            {paint.code || "N/A"}
                                                                        </span>
                                                                    </div>
                                                                    {paint.last_painted && (
                                                                        <div className="flex flex-col text-right">
                                                                            <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">Painted</span>
                                                                            <span className="text-xs font-medium text-foreground">
                                                                                {new Date(paint.last_painted).getFullYear()}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </GlassCard>
                                                </SwipeableCard>
                                            ))
                                    )}
                                </div>
                            
                        </div>
                    </motion.div>
                    </div>
                )}

                
                {activePopup === 'service' && (
                    <div 
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
                        style={{
                            paddingTop: "calc(env(safe-area-inset-top, 0px) + 1rem)",
                            paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)"
                        }}
                    >
                        <motion.div
                            key="popup-service"
                            initial={{ opacity: 0, scale: 0.9, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.9, y: 20 }}
                            transition={{ type: "spring", damping: 25, stiffness: 300 }}
                            className="w-full h-[90vh] max-w-2xl bg-white/95 backdrop-blur-xl border border-white/20 shadow-2xl rounded-3xl flex flex-col overflow-hidden pointer-events-auto"
                            style={{ maxHeight: "100%" }}
                        >
                        <div className="flex justify-between items-center p-6 border-b border-primary/10">
                            <h2 className="text-2xl font-bold text-foreground">Service History</h2>
                            <div className="flex gap-2">
                                <Button size="sm" onClick={() => setIsLogSheetOpen(true)} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-4">
                                    <Plus className="w-4 h-4 mr-1.5" /> Log
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => setActivePopup(null)} className="rounded-full bg-slate-100 hover:bg-slate-200">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x w-5 h-5"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                                </Button>
                            </div>
                        </div>
                        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
                            <select 
                                value={selectedApplianceFilter}
                                onChange={(e) => setSelectedApplianceFilter(e.target.value)}
                                className="w-full bg-white border border-slate-200 text-slate-700 text-sm rounded-xl focus:ring-primary focus:border-primary block p-2.5"
                            >
                                <option value="all">All Appliances</option>
                                {(appliances || []).map(app => (
                                    <option key={app.id} value={app.id}>{app.brand ? `${app.brand} ` : ''}{app.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 relative">
                            <div className={cn("space-y-3", isFeatureLocked('maintenance_history') && "opacity-50 blur-[2px] pointer-events-none")}>
                                    {serviceLogs.length === 0 ? (
                                        <GlassCard className="p-6">
                                            <div className="text-center">
                                                <Plus className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                                                <p className="text-sm text-muted-foreground">
                                                    No service history yet.
                                                </p>
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    Tap the + to log your first service.
                                                </p>
                                            </div>
                                        </GlassCard>
                                    ) : (
                                        displayLogs
                                            .filter(log => selectedApplianceFilter === "all" ? true : log.appliance_id === selectedApplianceFilter)
                                            .map((log, index) => {
                                                const isLocked = propertyLocked || isLogLocked(log.id);
                                                const appliance = log.appliance_id
                                                    ? appliances.find(a => a.id === log.appliance_id)
                                                    : null;
                                                const logTitle = appliance
                                                    ? `${appliance.brand || ''} ${appliance.name}`.trim()
                                                    : log.description || 'Service';

                                                return (
                                                <SwipeableCard
                                                    key={log.id}
                                                    onDelete={() => {
                                                        if (isLocked) return setShowUpgradeSheet(true);
                                                        setDeleteDialog({
                                                        open: true,
                                                        type: "service",
                                                        id: log.id,
                                                        name: logTitle,
                                                    })}}
                                                    onClick={() => isLocked ? setShowUpgradeSheet(true) : handleServiceLogClick(log)}
                                                    className="active-scale transition-transform cursor-pointer home-card-entry"
                                                    style={{ animationDelay: `${index * 50}ms` }}
                                                >
                                                    <GlassCard className={cn("p-4 relative overflow-hidden transition-all hover:home-glow", isLocked && "blur-[2px] opacity-70 grayscale pointer-events-none select-none")}>
                                                        <div className="flex items-start gap-4">
                                                            <div className="p-2 rounded-xl bg-orange-500/10 shrink-0">
                                                                <Wrench className="w-5 h-5 text-orange-600" />
                                                            </div>
                                                            <div className="flex-1 min-w-0 text-left">
                                                                <div className="flex justify-between items-start">
                                                                    <h3 className="font-bold text-foreground">{logTitle}</h3>
                                                                    {log.cost && (
                                                                        <span className="text-xs font-bold text-foreground bg-secondary/50 px-2 py-0.5 rounded-md">
                                                                            R{log.cost.toLocaleString()}
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                <div className="flex flex-wrap gap-2 mt-2">
                                                                    <div className="flex items-center gap-1 text-[10px] bg-orange-500/10 text-orange-600 px-2 py-0.5 rounded-full border border-orange-500/20">
                                                                        <Calendar className="w-3 h-3" />
                                                                        {new Date(log.date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                                    </div>
                                                                    {log.technician_name && (
                                                                        <div className="flex items-center gap-1 text-[10px] bg-slate-500/10 text-slate-600 px-2 py-0.5 rounded-full border border-slate-500/20">
                                                                            <PenTool className="w-3 h-3" />
                                                                            {log.technician_name}
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {log.description && (
                                                                    <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-orange-500/10 line-clamp-2 italic font-medium">
                                                                        "{log.description}"
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </GlassCard>
                                                    {isLocked && (
                                                        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-auto" onClick={(e) => { e.stopPropagation(); setShowUpgradeSheet(true); }}>
                                                            <div className="bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-md border border-primary/20 flex items-center gap-1.5">
                                                                <Lock className="w-3 h-3 text-primary" />
                                                                <span className="text-[10px] font-bold text-primary uppercase">Locked</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </SwipeableCard>
                                            )})
                                    )}
                                </div>

                                {isFeatureLocked('maintenance_history') && (
                                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center pt-8">
                                        <Button
                                            variant="outline"
                                            className="bg-white/80 hover:bg-white border-amber-500/50 text-amber-700 font-bold gap-2 shadow-lg"
                                            onClick={() => setShowUpgradeSheet(true)}
                                        >
                                            <Lock className="w-4 h-4 text-amber-500" />
                                            Unlock Service History
                                        </Button>
                                    </div>
                                )}
                            
                        </div>
                    </motion.div>
                    </div>
                )}

            </AnimatePresence>


            {/* Speed Dial FAB */}
            {!(activePopup || isLogSheetOpen || isPaintEditOpen || isAddApplianceOpen || isAddPaintOpen || isAddFurnitureOpen || isAddPlantOpen || editingPlant || editingInfo || deleteDialog?.open || editingAppliance || editingFurniture) && !propertyLocked && (
                <SpeedDialFAB options={speedDialOptions.map(opt => ({...opt, onClick: () => handleSpeedDialClick(opt.onClick)}))} />
            )}

            <UpgradePlanSheet
                open={showUpgradeSheet}
                onOpenChange={setShowUpgradeSheet}
                currentPlan={plan}
            />


            {/* Log Maintenance Sheet */}
            <LogMaintenanceSheet
                open={isLogSheetOpen}
                onOpenChange={setIsLogSheetOpen}
                onSuccess={() => {
                    refetchServiceLogs();
                    refetchAppliances();
                }}
            />

            {/* Paint Edit Sheet */}
            <PaintEditSheet
                open={isPaintEditOpen}
                onOpenChange={setIsPaintEditOpen}
                item={selectedPaint}
                onSave={handleSavePaint}
                onDelete={deletePaintCode}
            />

            {/* Add/Edit Appliance Sheet */}
            <AddApplianceSheet
                open={isAddApplianceOpen || !!editingAppliance}
                onOpenChange={(open) => {
                    if (!open) setEditingAppliance(null);
                    setIsAddApplianceOpen(open);
                }}
                initialData={editingAppliance}
                onSave={handleSaveAppliance}
            />

            {/* Add Paint Sheet */}
            <AddPaintSheet
                open={isAddPaintOpen}
                onOpenChange={setIsAddPaintOpen}
                onSave={handleAddPaint}
            />

            {/* Add/Edit Furniture Sheet */}
            <AddFurnitureSheet
                open={isAddFurnitureOpen || !!editingFurniture}
                onOpenChange={(open) => {
                    if (!open) setEditingFurniture(null);
                    setIsAddFurnitureOpen(open);
                }}
                initialData={editingFurniture}
                onSave={handleSaveFurniture}
                onSuccess={refetchFurniture}
            />

            <AddPlantSheet
                open={isAddPlantOpen || !!editingPlant}
                onOpenChange={(open) => {
                    if (!open) setEditingPlant(null);
                    setIsAddPlantOpen(open);
                }}
                onSuccess={refetchPlants}
                isLimitReached={isFeatureLocked('plant_limit', plants.length)}
                initialData={editingPlant}
                onUpdate={updatePlant}
                onDelete={deletePlant}
            />

            {/* Quick Info Edit Sheet */}
            <QuickInfoEditSheet
                open={!!editingInfo}
                onOpenChange={(open) => !open && setEditingInfo(null)}
                infoType={editingInfo?.infoType || ""}
                label={editingInfo?.label || ""}
                currentValue={editingInfo?.value || ""}
                currentSubValue={editingInfo?.subValue}
                isCustom={editingInfo?.isCustom}
                onSave={handleSaveQuickInfo}
            />

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={deleteDialog?.open} onOpenChange={(open) => !open && setDeleteDialog(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete {deleteDialog?.type === "appliance" ? "Appliance" : deleteDialog?.type === "paint" ? "Paint Code" : deleteDialog?.type === "quick_info" ? "Info Item" : deleteDialog?.type === "plant" ? "Plant" : deleteDialog?.type === "furniture" ? "Furniture" : "Service Log"}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Are you sure you want to delete "{deleteDialog?.name}"?
                            {deleteDialog?.type === "appliance" && (
                                <span className="block mt-2 text-destructive">
                                    This will also delete all service history associated with this appliance.
                                </span>
                            )}
                            {deleteDialog?.type === "furniture" && (
                                <span className="block mt-2 text-destructive">
                                    This will also delete all service history associated with this furniture item.
                                </span>
                            )}
                            This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteConfirm}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

// Force HMR update
export default HomeRecords;
